import { useWindowResize } from 'hooks';
import { Typography } from 'lib/components';
import React, { useEffect, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { JobLog } from 'types';
import adjustTime from '../../lib/adjustTime';
import { useSelectSettings } from '../../store';
import classes from './index.module.scss';

/**
 * Props interface for the Timeline component
 */
interface Props {
	/** Array of job logs to display in the timeline */
	logs: JobLog[];
}

/**
 * Helper function to sort logs by ID in descending order (newest first)
 * Creates a new array to avoid mutating the original
 *
 * @param logs - Array of logs to sort
 * @returns Sorted array of logs
 */
const sortLogs = (logs: JobLog[]) => [...logs].sort((a, b) => b.id - a.id);

/**
 * Timeline component
 * Displays a vertical timeline of job logs with timestamps and messages
 * Sorts logs by ID (newest first) and renders them in a scrollable container
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const Timeline = ({ logs }: Props) => {
	// Settings data from Redux store (for time zone adjustments)
	const settingsState = useSelectSettings();

	// Responsive design helpers
	const { isSmallerDevice } = useWindowResize();
	// State for sorted logs (newest first)
	const [sortedLogs, setSortedLogs] = useState<JobLog[]>([]);

	/**
	 * Sort logs when logs prop changes
	 * Ensures logs are always displayed newest first
	 */
	useEffect(() => {
		if (logs.length === 0) return;

		setSortedLogs(sortLogs(logs));
	}, [logs]);

	return (
		<div className={classes['wrapper']} style={{ marginTop: '0.5rem', flex: 1, padding: 0 }}>
			{/* Scrollable container for timeline */}
			<Scrollbars
				renderThumbVertical={(props) => <div {...props} className={classes['vertical-scroll-thumb']} />}
			>
				<div className={classes['timeline']}>
					{/* Vertical line that connects all timeline dots */}
					<div
						className={classes['vertical-strip']}
						style={{
							height: logs.length === 0 ? '100%' : `${logs.length * 3.5}rem`,
						}}
					/>
					{/* Map through sorted logs and render each timeline entry */}
					{sortedLogs.map(({ id, message, createdAt }) => (
						<div key={id} className={classes['row']}>
							{/* Timestamp column - adjusted for time zone */}
							<Typography variant={isSmallerDevice ? 'h5' : 'h6'} className={classes['first-col']}>
								{adjustTime(settingsState, createdAt, 'MM/DD/YYYY - hh:mm A').format(
									'MM/DD/YYYY hh:mm A'
								)}
							</Typography>
							{/* Timeline dot marker */}
							<div className={classes['dot']} />
							{/* Message column */}
							<div className={classes['second-col']}>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'} weight="medium">
									{message}
								</Typography>
							</div>
						</div>
					))}
				</div>
			</Scrollbars>
		</div>
	);
};

export default Timeline;
